import dynamic from 'next/dynamic'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { useGlobalDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'
import { useUserStoreLocationName } from '@/features/shop/services/ShoppingStore/hooks'
import { useUserId, useUserStoreImage } from '@/features/account/services/User/hooks'
import { Column, Row } from '@shipt/design-system-layouts'
import { Body, typographyAttributes } from '@shipt/design-system-typography'
import { TextButton } from '@shipt/design-system-buttons'
import {
  useIsGuestWithZipOnly,
  useOpenGuestAddressModalForPLA,
  usePlaPopoverEligibility,
  useSubmitZipForPlaPopover,
} from '@/features/shop/components/ProductDetail/PLA/hooks'
import { screenSizes } from '@shipt/design-system-themes'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { HDivider } from '@/features/shared/components/Dividers'
import { DynamicGlobalAddressModal } from '@/features/account/components/Modal/GlobalAddressModal/DynamicGlobalAddressModal'
import { PDPMembershipBanner } from '@/features/shop/components/ProductDetail/PDPMembershipBanner/PDPMembershipBanner'

const PlaPricingPopover = dynamic(
  () => import('./PlaPricingPopover').then((m) => m.PlaPricingPopover),
  { ssr: false },
)
const ZipConfirmationPopover = dynamic(
  () =>
    import('@/features/account/components/ZipConfirmationPopover').then(
      (m) => m.ZipConfirmationPopover,
    ),
  { ssr: false },
)

export const ProductDetailPlaContent = ({ productId }: { productId: number }) => {
  const userId = useUserId()
  const { openDialog } = useHandleDialog()
  const { image: storeImage } = useUserStoreImage()
  const storeLocationName = useUserStoreLocationName()
  const isGuestWithZipOnly = useIsGuestWithZipOnly()
  const isPlaPopoverEligible = usePlaPopoverEligibility(productId)
  const { street1, zip_code } = useGlobalDefaultShoppingAddress()
  const openGuestAddressModal = useOpenGuestAddressModalForPLA()
  const submitZipForPlaPopover = useSubmitZipForPlaPopover(productId)

  const handleOpenAddressModal = () =>
    userId ? openDialog(DynamicGlobalAddressModal) : openGuestAddressModal()

  return (
    <div>
      {!userId && (
        <Column>
          <PDPMembershipBanner />
          <ContentHDivider />
        </Column>
      )}
      <Container $isGuestWithZipOnly={isGuestWithZipOnly}>
        <Body strong size="xl">
          Get same-day delivery from
        </Body>
        <ContentWrapperRow align="center" spacing="md">
          <div>
            <StoreImage src={storeImage} width={40} height={40} alt="" />
            {!!isPlaPopoverEligible && (
              <PlaPricingPopoverWrapper>
                <PlaPricingPopover align="start" />
              </PlaPricingPopoverWrapper>
            )}
          </div>
          <InformationWrapper>
            <Body strong>{storeLocationName}</Body>
            <AvailableText variant="secondary">
              {isGuestWithZipOnly ? 'Available in' : 'Deliver to'}{' '}
            </AvailableText>
            {isGuestWithZipOnly ? (
              <ZipConfirmationPopover
                onSubmit={submitZipForPlaPopover}
                displaySublocation="pdp_pla_content"
              >
                {(props) => <AddressButton {...props}>{zip_code}</AddressButton>}
              </ZipConfirmationPopover>
            ) : (
              <AddressButton
                {...typographyAttributes({ isPII: true })}
                onClick={handleOpenAddressModal}
              >
                {street1}
              </AddressButton>
            )}
          </InformationWrapper>
        </ContentWrapperRow>
      </Container>
    </div>
  )
}

/**
 * Hide the delivery text on tablet+ for users who are not a guest user with only a zip code
 * and always show it to all users on mobile view since the store logo is hidden on the header
 * and the product team wants the store logo to have more visibility for the PLA flow.
 */
const Container = styled.div<{ $isGuestWithZipOnly: boolean }>`
  @media ${screenSizes.tablet} {
    display: ${({ $isGuestWithZipOnly }) => !$isGuestWithZipOnly && 'none'};
  }
`

const ContentWrapperRow = styled(Row)`
  width: 100%;
  padding: 16px;
  text-align: left;
  margin-top: 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid ${({ theme }) => theme.color.border.base.neutral.subtle};
`

const StoreImage = styled(Image)`
  min-height: 2.5rem;
  min-width: 2.5rem;
`

const PlaPricingPopoverWrapper = styled.div`
  @media ${screenSizes.tablet} {
    display: none;
  }
`

const InformationWrapper = styled.div`
  width: 100%;
`

const AvailableText = styled(Body)`
  display: inline;
`

const AddressButton = styled(TextButton)`
  display: inline-block;
  text-decoration: underline;
  color: ${({ theme }) => theme.color.content.action.secondary};
`

const ContentHDivider = styled(HDivider)`
  margin: 2rem 0;

  @media ${screenSizes.tablet} {
    margin: 1.5rem 0;
  }
`
